import axios from 'axios';

// For production deployment, use relative URL (same domain)
// For development, use REACT_APP_BACKEND_URL environment variable
const getApiBase = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If env variable is set and not empty, use it
  if (envUrl && envUrl.trim() !== '') {
    return envUrl + '/api';
  }
  
  // Otherwise use relative URL (for production deployment)
  return '/api';
};

const API_BASE = getApiBase();

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request/Response interceptors
apiClient.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Mobile Gate Services
export const mobileGateService = {
  submitMobileNumber: async (mobileNumber) => {
    try {
      const response = await apiClient.post('/mobile-gate', {
        mobile_number: mobileNumber
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to submit mobile number');
    }
  },

  // OTP Services
  sendOTP: async (mobileNumber) => {
    try {
      const response = await apiClient.post('/otp/send', {
        mobile_number: mobileNumber
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to send OTP');
    }
  },

  verifyOTP: async (mobileNumber, otpCode) => {
    try {
      const response = await apiClient.post('/otp/verify', {
        mobile_number: mobileNumber,
        otp_code: otpCode
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to verify OTP');
    }
  },

  resendOTP: async (mobileNumber) => {
    try {
      const response = await apiClient.post('/otp/resend', {
        mobile_number: mobileNumber
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to resend OTP');
    }
  }
};

// Books Services
export const booksService = {
  getBooksList: async () => {
    try {
      const response = await apiClient.get('/books');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch books');
    }
  }
};

// Payment Services (Updated)
export const paymentService = {
  createOrder: async (orderData) => {
    try {
      const response = await apiClient.post('/payment/create-order', orderData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to create payment order');
    }
  },

  verifyPayment: async (paymentData) => {
    try {
      const response = await apiClient.post('/payment/verify', paymentData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Payment verification failed');
    }
  }
};

// Purchase Services
export const purchaseService = {
  getPurchaseDetails: async (purchaseId) => {
    try {
      const response = await apiClient.get(`/purchase/${purchaseId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Failed to fetch purchase details');
    }
  }
};

// Download Services
export const downloadService = {
  downloadPDF: async (purchaseId, bookId) => {
    try {
      const response = await apiClient.post('/download-pdf', {
        purchase_id: purchaseId,
        book_id: bookId
      }, {
        responseType: 'blob'
      });

      // Create blob URL for download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Extract filename from headers or create default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Marriage_101_Book.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);

      return { success: true, message: 'Download started successfully', filename };
      
    } catch (error) {
      throw new Error(error.response?.data?.detail || 'Download failed');
    }
  }
};

// Health Services
export const healthService = {
  checkHealth: async () => {
    try {
      const response = await apiClient.get('/health');
      return response.data;
    } catch (error) {
      throw new Error('Health check failed');
    }
  }
};

export default apiClient;